import React, { useState } from 'react';
import { Plus, ListPlus } from 'lucide-react';
import { BulkAddModal } from './BulkAddModal';

interface AddEntryInputProps {
  onAddEntry: (label: string) => void;
  onBulkAdd: (labels: string[]) => void;
  disabled?: boolean;
}

export const AddEntryInput: React.FC<AddEntryInputProps> = ({
  onAddEntry,
  onBulkAdd,
  disabled = false,
}) => {
  const [inputVal, setInputVal] = useState('');
  const [isBulkOpen, setIsBulkOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputVal.trim() || disabled) return;

    // Check if user pasted multi-line into the single input
    if (inputVal.includes('\n') || inputVal.includes('\r')) {
      const lines = inputVal
        .split(/[\r\n]+/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length > 0) {
        onBulkAdd(lines);
        setInputVal('');
        return;
      }
    }

    onAddEntry(inputVal.trim());
    setInputVal('');
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData('text');
    if (pasteData.includes('\n') || pasteData.includes('\r')) {
      e.preventDefault();
      const lines = pasteData
        .split(/[\r\n]+/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length > 0) {
        onBulkAdd(lines);
        setInputVal('');
      }
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onPaste={handlePaste}
            disabled={disabled}
            placeholder="Add name (press Enter)..."
            style={{
              paddingRight: '36px',
              height: '42px',
              fontSize: '0.9rem',
            }}
          />
          <button
            type="button"
            onClick={() => setIsBulkOpen(true)}
            disabled={disabled}
            title="Bulk paste multiple names"
            aria-label="Bulk paste multiple names"
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '6px',
              color: 'var(--text-muted)',
              borderRadius: '6px',
            }}
          >
            <ListPlus size={18} />
          </button>
        </div>

        <button
          type="submit"
          disabled={!inputVal.trim() || disabled}
          className="btn-primary"
          style={{
            height: '42px',
            padding: '0 1rem',
            fontSize: '0.9rem',
            whiteSpace: 'nowrap',
          }}
        >
          <Plus size={18} />
          Add
        </button>
      </form>

      <BulkAddModal
        isOpen={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        onBulkAdd={onBulkAdd}
      />
    </>
  );
};
